import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import { authorIdOf, extractContactIdsFromMessage, getChatTimestamp, getDisplayName, getDisplayNamesMap, mapUserMessage } from "../team-inbox-helpers";
import { CommonRootState } from '@common/types/common-root-state-type';
import { createSelector, createEntityAdapter } from '@reduxjs/toolkit';
import { waContactsCacheSelectors } from "../../../common/reducers/DBServiceThunks";

// Create entity adapter for team inbox cache selectors
// This matches the adapter configuration in DBServiceReducers
const teamInboxEntityAdapter = createEntityAdapter({
    selectId: (item: any) => item?._id ?? item?.tempId ?? null,
});

// Create selectors scoped to the team inbox cache reducer states
export const teamInboxChatsCacheSelectors = teamInboxEntityAdapter.getSelectors(
    (state: CommonRootState) => state.DBTeamInboxChatsCacheReducer
);

export const teamInboxMessagesCacheSelectors = teamInboxEntityAdapter.getSelectors(
    (state: CommonRootState) => state.DBTeamInboxMessagesCacheReducer
);


//*** baseSelectors  ***


// Selector for a chat by its ID
export const selectChatById = (state: CommonRootState, chatId: string) =>
    teamInboxChatsCacheSelectors.selectById(state, chatId);


export const selectMessageById = (state: CommonRootState, messageId: string) =>
    teamInboxMessagesCacheSelectors.selectById(state, messageId);


// Selector for chatViewMode - returns only the chat ID
export const selectCurrentChatId = (state: CommonRootState) =>
    state.TeamInboxReducer?.chatViewMode?.chatId ?? null;


// Selector for determining if a chat is active
export const selectIsChatActive = (state: CommonRootState): boolean => {
    const { type, chatId } = state.TeamInboxReducer?.chatViewMode ?? {};
    return type === "chat" && !!chatId;
};





// *** computedSelectors ***

// Helper function to check if a chat is a standard WhatsApp chat (not lid server)
// Used for the archived view list - shows all standard archived chats
const isStandardWhatsAppChat = (chat: any): boolean => {
    const chatId = chat?._id || chat?._serialized || '';
    // Standard WhatsApp servers are 'c.us' (contacts) and 'g.us' (groups)
    // 'lid' server is a different chat type that shouldn't be displayed
    return !chatId.endsWith('@lid');
};

// Newsletters (Channels). WhatsApp Web keeps these out of the main chat list
// and under a dedicated "Channels" section — Team Inbox mirrors that: excluded
// from the inbox list, shown only in the Channels view.
const isNewsletterChat = (chat: any): boolean => {
    const chatId = chat?._id || chat?._serialized || chat?.id || '';
    return chatKindOf(chatId) === 'channel';
};

/** Resolve a chat's JID string across every shape the inbox holds:
 *  the BtSlimChat cache entry (`chatId`/`_id`/`_serialized`) and the raw
 *  WAWeb `tempChatData` fallback (`id._serialized`). */
const getChatJid = (chat: any): string => {
    if (typeof chat?.chatId === 'string') return chat.chatId;
    if (typeof chat?._id === 'string') return chat._id;
    if (typeof chat?._serialized === 'string') return chat._serialized;
    if (typeof chat?.id?._serialized === 'string') return chat.id._serialized;
    if (typeof chat?.id === 'string') return chat.id;
    return '';
};

/** Group detection by JID suffix — shape-independent, so it works for both
 *  the new `chatType`-carrying cache entries and the raw `tempChatData`
 *  fallback (mirrors `isNewsletterChat`). Replaces the removed `isGroup`
 *  boolean now that the contract uses `chatType`. */
export const isGroupChat = (chat: any): boolean => chatKindOf(getChatJid(chat)) === 'group';

// Selector for chats sorted by timestamp (newest first)
export const selectChatsSortedByTimestamp = createSelector(
    [(state: CommonRootState) => teamInboxChatsCacheSelectors.selectAll(state)],
    (chats) => chats
        .slice()
        .sort((a, b) => getChatTimestamp(b) - getChatTimestamp(a))
);

// Active label-view filter computed by filterChatsByLabelIdThunk. On WhatsApp
// Web the views are applied by manipulating the WAWeb UI; the dashboard's
// Team Inbox list applies the same computed filter here.
const selectChatListFilter = (state: CommonRootState) =>
    (state as any).LabelReducer?.chatListFilter ?? null;

const chatMatchesListFilter = (chat: any, filter: { type: 'UNREAD' } | { type: 'IDS'; chatIds: string[] } | null): boolean => {
    if (!filter) return true;
    if (filter.type === 'UNREAD') return (chat.unreadCount || 0) > 0;
    const chatId = chat?._serialized || chat?._id || chat?.id;
    return filter.chatIds.includes(chatId);
};

// Selector for non-archived chats sorted like WhatsApp Web:
// 1. Pinned chats first (sorted by timestamp among themselves)
// 2. Non-pinned chats sorted by timestamp (newest first)
export const selectNonArchivedChatsSortedByTimestamp = createSelector(
    [
        (state: CommonRootState) => teamInboxChatsCacheSelectors.selectAll(state),
        selectChatListFilter,
    ],
    (chats, chatListFilter) => {
        const nonArchivedChats = chats.filter(chat =>
            !chat.archived && !isNewsletterChat(chat) && chatMatchesListFilter(chat, chatListFilter));

        // Separate pinned and non-pinned chats
        const pinnedChats = nonArchivedChats
            .filter(chat => chat.pinned)
            .sort((a, b) => getChatTimestamp(b) - getChatTimestamp(a));

        const nonPinnedChats = nonArchivedChats
            .filter(chat => !chat.pinned)
            .sort((a, b) => getChatTimestamp(b) - getChatTimestamp(a));

        // Return pinned chats first, then non-pinned
        return [...pinnedChats, ...nonPinnedChats];
    }
);

// === Channels (newsletters) ===
// Newsletter chats, sorted newest-first — backing list for the Channels view.
export const selectChannelChatsSortedByTimestamp = createSelector(
    [(state: CommonRootState) => teamInboxChatsCacheSelectors.selectAll(state)],
    (chats) => chats
        .filter(chat => !chat.archived && isNewsletterChat(chat))
        .sort((a, b) => getChatTimestamp(b) - getChatTimestamp(a))
);

// Count for the "Channels" row badge (total newsletters).
export const selectChannelChatsCount = createSelector(
    [(state: CommonRootState) => teamInboxChatsCacheSelectors.selectAll(state)],
    (chats) => chats.filter(chat => !chat.archived && isNewsletterChat(chat)).length
);

export const selectIsChannelsView = (state: CommonRootState) =>
    (state as any).TeamInboxReducer?.isChannelsView ?? false;

// Selector for total archived chats count - used to determine if archived row should be shown
export const selectTotalArchivedChatsCount = createSelector(
    [(state: CommonRootState) => teamInboxChatsCacheSelectors.selectAll(state)],
    (chats) => {
        const archivedChats = chats.filter(chat =>
            chat.archived &&
            isStandardWhatsAppChat(chat)
        );
        return archivedChats.length;
    }
);

// Selector for archived chats with unread count - for the badge number
// This matches WhatsApp's behavior where the badge shows chats with unread, not total archived
export const selectArchivedChatsCount = createSelector(
    [(state: CommonRootState) => teamInboxChatsCacheSelectors.selectAll(state)],
    (chats) => {
        // Filter for archived chats that have unread messages
        const archivedChatsWithUnread = chats.filter(chat =>
            chat.archived &&
            isStandardWhatsAppChat(chat) &&
            (chat.unreadCount > 0)
        );
        return archivedChatsWithUnread.length;
    }
);

// Selector for archived chats sorted by timestamp (for archived view)
// Excludes lid server chats to match WhatsApp's archived list
export const selectArchivedChatsSortedByTimestamp = createSelector(
    [(state: CommonRootState) => teamInboxChatsCacheSelectors.selectAll(state)],
    (chats) => chats
        .filter(chat => chat.archived && isStandardWhatsAppChat(chat))
        .slice()
        .sort((a, b) => getChatTimestamp(b) - getChatTimestamp(a))
);

// Selector for isArchivedView state
export const selectIsArchivedView = (state: CommonRootState) =>
    state.TeamInboxReducer?.isArchivedView ?? false;

// Message staged for forwarding (set by the hover-action forward button;
// consumed by the wave-2 forward picker dialog).
export const selectForwardingMessage = (state: CommonRootState) =>
    (state as any).TeamInboxReducer?.forwardingMessage ?? null;

// Message currently being replied to (WA reply flow; composer preview bar +
// userMessage.quotedMessageId at send time).
export const selectReplyingToMessage = (state: CommonRootState) =>
    (state as any).TeamInboxReducer?.replyingToMessage ?? null;

// Selector for the current active chat in chatViewMode
export const selectCurrentWaChat = createSelector(
    [
        (state: CommonRootState) => teamInboxChatsCacheSelectors.selectEntities(state),
        (state: CommonRootState) => state.TeamInboxReducer?.chatViewMode ?? null
    ],
    (entities, chatViewMode) => {
        const chatId = chatViewMode?.chatId ?? null;
        if (!chatId) return null;

        return (
            entities[chatId] ||
            (chatViewMode?.type === "chat" ? chatViewMode?.tempChatData : null)
        );
    }
);

// Selector for messages of the current chat (sorted by timestamp - newest at the bottom)
export const selectCurrentChatMessages = createSelector(
    [
        (state: CommonRootState) => teamInboxMessagesCacheSelectors.selectAll(state),
        (state: CommonRootState) => selectCurrentChatId(state)
    ],
    (messages, currentChatId) => {
        if (!currentChatId) return [];

        // Filter messages that belong to the current chat using the stored chatId
        const filteredMessages = messages.filter(msg => msg.chatId === currentChatId);

        return filteredMessages
            .slice()
            .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
    }
);

// Selector to check if current chat is read-only for the current user
// The engine's `group.isReadOnly` already encodes the contract rule (not a
// member, or announce-only and not an admin) on both engines.
export const selectIsCurrentChatReadOnly = createSelector(
    [(state: CommonRootState) => selectCurrentWaChat(state)],
    (currentChat) => !!currentChat?.group?.isReadOnly,
);


// *** selectorFactories ***


export const makeSelectNameForDisplay = (contactId: string, optionalNotifyName?: string) =>
    createSelector(
        [
            (state: CommonRootState) => state.TeamInboxReducer?.waSessionPhoneNumber ?? null,
            (state: CommonRootState) => waContactsCacheSelectors.selectById(state, contactId)
        ],
        (waSessionPhoneNumber, contact) => {
            if (!contactId) return '';

            // Pass notifyName for display name resolution - used for group message senders
            return getDisplayName({
                _serialized: contactId,
                name: contact?.name,
                pushname: contact?.pushname,
                notifyName: optionalNotifyName || contact?.notifyName
            }, waSessionPhoneNumber ?? '');
        }
    );

export const makeSelectMessageDraftById = (messageId: string | null) => {
    const selectDisplayNames = makeSelectDisplayNamesByMessageId(messageId ?? '');

    return createSelector(
        [
            (state: CommonRootState) => selectMessageById(state, messageId ?? ''),
            (state: CommonRootState) => state.AddScheduledMessageButtonReducer.messageDraft,
            (state: CommonRootState) => state.TeamInboxReducer?.waSessionPhoneNumber ?? null,
            (state: CommonRootState) => selectDisplayNames(state)
        ],
        (message, messageDraft, waSessionPhoneNumber, displayNames) => {
            if (!messageId) return messageDraft;
            if (!message) return null;
            return mapUserMessage(message, waSessionPhoneNumber ?? '', displayNames);
        }
    );
}

export const makeSelectDisplayNamesByContactIds = (contactIds: string[]) =>
    createSelector(
        [
            (state: CommonRootState) => state.TeamInboxReducer?.waSessionPhoneNumber ?? null,
            (state: CommonRootState) => waContactsCacheSelectors.selectEntities(state),
        ],
        (waSessionPhoneNumber, contactsMap) => {
            return getDisplayNamesMap(contactIds, contactsMap, waSessionPhoneNumber ?? '');
        }
    );

export const makeSelectDisplayNamesByMessageId = (messageId: string) =>
    createSelector(
        [
            (state: CommonRootState) => state.TeamInboxReducer?.waSessionPhoneNumber ?? null,
            (state: CommonRootState) => selectMessageById(state, messageId),
            (state: CommonRootState) => waContactsCacheSelectors.selectEntities(state),
        ],
        (waSessionPhoneNumber, message, contactsMap) => {
            if (!message) return {};

            const contactIds = extractContactIdsFromMessage({
                author: authorIdOf(message),
                type: message.type,
                subtype: message.subtype,
                revokeSender: message.revokeSender?._serialized,
                recipients: message.recipients,
            });

            return getDisplayNamesMap(contactIds, contactsMap, waSessionPhoneNumber ?? '');
        }
    );